/**
 * ExportDropdown - Export menu for portfolio data (TASK-407).
 *
 * Replaces the legacy PDF + CSV menu. New options:
 *   - 'client-pptx' → LLM-generated client-facing PPTX (no fees / P&L)
 *   - 'full-pptx'   → advisor copy with full numbers
 *   - 'csv'         → unchanged, client-side CSV export
 *
 * Adds a language picker (en/de/fr) inside the menu. Parent owns the actual
 * export logic and toggles `loading` while a PPTX generation is in flight.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Download, FileText, File, Table, ChevronDown, Loader2, Globe } from 'lucide-react';

export type ExportOption = 'client-pptx' | 'full-pptx' | 'csv';
export type ExportLanguage = 'en' | 'de' | 'fr';

export interface ExportDropdownProps {
  onExport: (option: ExportOption, language: ExportLanguage) => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  enableCsv?: boolean;
}

const LANGUAGES: Array<{ code: ExportLanguage; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
];

const ExportDropdown: React.FC<ExportDropdownProps> = ({
  onExport,
  className = '',
  disabled = false,
  loading = false,
  enableCsv = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState<ExportLanguage>('en');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleExport = (option: ExportOption) => {
    setIsOpen(false);
    onExport(option, language);
  };

  const buttonDisabled = disabled || loading;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        disabled={buttonDisabled}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          buttonDisabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-kielis-navy text-white hover:bg-kielis-blue-dark shadow-sm'
        }`}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {loading ? 'Generating…' : 'Export'}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="px-3 pt-3 pb-2 border-b border-gray-100">
            <div className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-2">
              <Globe className="w-3.5 h-3.5" />
              Language
            </div>
            <div className="flex gap-1">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`flex-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                    language === lang.code
                      ? 'bg-kielis-navy text-white'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-2">
            <button
              onClick={() => handleExport('client-pptx')}
              className="w-full flex items-start gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900">Client Deck (PPTX)</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  Branded slide deck for client meetings. Excludes P&amp;L, fees, cost basis.
                </div>
              </div>
            </button>

            <div className="my-1 border-t border-gray-100" />

            <button
              onClick={() => handleExport('full-pptx')}
              className="w-full flex items-start gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <File className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900">Full Deck (PPTX)</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  Advisor copy with all metrics, P&amp;L, fees, transactions appendix.
                </div>
              </div>
            </button>

            {enableCsv && (
              <>
                <div className="my-1 border-t border-gray-100" />
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full flex items-start gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <Table className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900">Data Export (CSV)</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      Raw position data for Excel analysis. No generation delay.
                    </div>
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportDropdown;
